import { mathService } from '@approvalmax/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import uniqueId from 'lodash/uniqueId';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { useCallback, useMemo } from 'react';
import { useLifecycles } from 'react-use';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteJournalEntryFieldSettings,
} from 'shared/hooks/useNetSuiteFieldSettings';

import { getContext } from '../../../selectors/pageSelectors';
import { convertCustomFieldsToState, getCustomFieldsTransfer, setCurrencyPrecision } from '../helpers';
import {
    useChangeNetSuiteRequest,
    useCurrencyPrecision,
    useCustomFields,
    useResetJournalEntryFormState,
} from '../hooks';
import { isFutureDate } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteCustomFieldsState, netSuiteItemLineNumberState, netSuiteShowErrorsState } from '../resources/states';
import {
    getJournalEntryValidationErrors,
    mapJournalEntryItemLinesTransfer,
} from './NetSuiteJournalEntryRequestForm.helpers';
import {
    netSuiteJournalEntryFieldValuesState,
    netSuiteJournalEntryItemListState,
} from './NetSuiteJournalEntryRequestForm.states';
import { NetSuiteJournalEntryAmounts, NetSuiteJournalEntryFields } from './NetSuiteJournalEntryRequestForm.types';

export const useNetSuiteJournalEntryFields = () => {
    const setFieldValues = useSetAtom(netSuiteJournalEntryFieldValuesState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const updateFieldsBulk = useCallback(
        (data: Partial<NetSuiteJournalEntryFields>, shouldDispatchChangeEvent = true) => {
            setFieldValues((current) => {
                if (current !== null) {
                    return {
                        ...current,
                        ...data,
                    };
                }

                return current;
            });

            if (shouldDispatchChangeEvent) {
                changeNetSuiteRequest();
            }
        },
        [changeNetSuiteRequest, setFieldValues]
    );

    const onFieldChange = useCallback(
        <K extends keyof NetSuiteJournalEntryFields>(key: K, value: NetSuiteJournalEntryFields[K]) => {
            setFieldValues((current) => ({
                ...current,
                [key]: value,
            }));

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, setFieldValues]
    );

    return {
        updateFieldsBulk,
        onFieldChange,
    };
};

export const useNetSuiteJournalEntryAmounts = () => {
    const fieldValues = useAtomValue(netSuiteJournalEntryFieldValuesState);
    const journalEntryItemList = useAtomValue(netSuiteJournalEntryItemListState);

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const amounts: NetSuiteJournalEntryAmounts = useMemo(() => {
        let debitSubTotal = 0;
        let creditSubTotal = 0;
        let debitTotal = 0;
        let creditTotal = 0;
        let debitTotalTax = 0;
        let creditTotalTax = 0;

        journalEntryItemList.forEach((item) => {
            if (item.debitAmount && item.debitAmount > 0) {
                debitSubTotal = mathService.add(debitSubTotal, item.debitAmount);
                debitTotalTax = mathService.add(debitTotal, item.taxAmount ?? 0);
            }

            if (item.creditAmount && item.creditAmount > 0) {
                creditSubTotal = mathService.add(creditSubTotal, item.creditAmount);
                creditTotalTax = mathService.add(creditTotal, item.taxAmount ?? 0);
            }
        });
        debitTotal = mathService.add(debitSubTotal, debitTotalTax);
        creditTotal = mathService.add(creditSubTotal, creditTotalTax);

        return {
            debitSubTotal: setCurrencyPrecision(debitSubTotal, currencyPrecision),
            creditSubTotal: setCurrencyPrecision(creditSubTotal, currencyPrecision),
            debitTotal: setCurrencyPrecision(debitTotal, currencyPrecision),
            creditTotal: setCurrencyPrecision(creditTotal, currencyPrecision),
            debitTotalTax: setCurrencyPrecision(debitTotalTax, currencyPrecision),
            creditTotalTax: setCurrencyPrecision(creditTotalTax, currencyPrecision),
        };
    }, [journalEntryItemList, currencyPrecision]);

    return amounts;
};

export const useNetSuiteJournalEntryLifecycles = (request: domain.NetSuiteJournalEntryRequest) => {
    const { updateFieldsBulk } = useNetSuiteJournalEntryFields();
    const { currencies, customFields } = useSelector(getNetSuiteContext);

    const { setCustomFieldValuesBulk } = useCustomFields(customFields);
    const setNextLineNumber = useSetAtom(netSuiteItemLineNumberState);
    const setItemList = useSetAtom(netSuiteJournalEntryItemListState);

    const resetJournalEntryFormState = useResetJournalEntryFormState();

    useLifecycles(
        () => {
            const { customFields, lines, ...fields } = request.details;

            const transactionDate = fields.transactionDate || moment().utc().toISOString();
            const currency = currencies.find(({ id, ISOCode }) =>
                fields.currency ? fields.currency.id === id : request.companyCurrency === ISOCode
            );

            updateFieldsBulk(
                {
                    ...fields,
                    transactionDate,
                    currency,
                    exchangeRate:
                        request.exchangeRateSource === domain.ExchangeRateSource.Manual ? fields.exchangeRate : null,
                },
                false
            );

            if (lines.length) {
                const maxLineNumber = Math.max(...lines.map((line) => line.lineNumber ?? 0));

                setNextLineNumber(maxLineNumber + 1);

                setItemList(
                    lines.map((itemListItem) => {
                        const expandedLine = {
                            ...itemListItem,
                            isEmpty: false,
                            checked: false,
                            id: uniqueId('netSuiteJournalEntryLine-'),
                        };

                        return expandedLine;
                    })
                );
            }

            if (customFields) {
                setCustomFieldValuesBulk(customFields.length > 0 ? convertCustomFieldsToState(customFields) : {});
            }
        },
        () => {
            resetJournalEntryFormState();
        }
    );
};

export const useGetActionsBlockProps = (
    request: domain.NetSuiteJournalEntryRequest,
    defaultCurrencyRate: number | undefined,
    attachmentsFieldSettings: ReturnType<typeof useNetSuiteAttachmentsFieldSettings>
) => {
    const { currencies, customFields, documentFields } = useSelector(getNetSuiteContext);
    const pageContext = useSelector(getContext);

    const setShowErrors = useSetAtom(netSuiteShowErrorsState);
    const fieldValues = useAtomValue(netSuiteJournalEntryFieldValuesState);
    const itemList = useAtomValue(netSuiteJournalEntryItemListState);
    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);

    const journalEntryFieldSettings = useNetSuiteJournalEntryFieldSettings(documentFields);

    const requestCurrencyISOCode = useMemo(() => {
        return currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode ?? null;
    }, [currencies, fieldValues.currency]);

    const getRequestTransfer = useCallback((): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            currency: currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode,
            exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate || 1,
            exchangeRateSource:
                typeof fieldValues.exchangeRate === 'number'
                    ? domain.ExchangeRateSource.Manual
                    : domain.ExchangeRateSource.NetSuite,
            exchangeRateDate: isFutureDate(fieldValues.transactionDate)
                ? moment.utc().format('yyyy-MM-DD')
                : moment.utc(fieldValues.transactionDate).format('yyyy-MM-DD'),
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: request.requestNote.value,
            templateVersion: pageContext.templateVersion,
            companyId: request.companyId,
        };

        transfer.netSuiteJournalEntryDetails = {
            reference: fieldValues.reference ?? null,
            currencyId: fieldValues.currency?.id ?? null,
            exchangeRate: fieldValues.exchangeRate ?? defaultCurrencyRate ?? null,
            date: fieldValues.transactionDate ?? null,
            reversalDate: fieldValues.reversalDate ?? null,
            isReversalDefer: fieldValues.reversalDefer ?? false,
            classId: fieldValues.class?.id ?? null,
            locationId: fieldValues.location?.id ?? null,
            departmentId: fieldValues.department?.id ?? null,
            memo: fieldValues.memo ?? null,
            subsidiaryId: fieldValues.subsidiary?.id ?? null,
            lines: mapJournalEntryItemLinesTransfer(itemList),
            customFields: getCustomFieldsTransfer(Object.values(customFieldsValues)),
        };

        return transfer;
    }, [
        request.id,
        request.attachments,
        request.requestNote.value,
        request.companyId,
        currencies,
        fieldValues.exchangeRate,
        fieldValues.transactionDate,
        fieldValues.reference,
        fieldValues.currency?.id,
        fieldValues.reversalDate,
        fieldValues.reversalDefer,
        fieldValues.class?.id,
        fieldValues.location?.id,
        fieldValues.department?.id,
        fieldValues.memo,
        fieldValues.subsidiary?.id,
        defaultCurrencyRate,
        pageContext.templateVersion,
        itemList,
        customFieldsValues,
    ]);

    const getValidationErrors = useCallback(async () => {
        setShowErrors(true);

        return getJournalEntryValidationErrors({
            fields: fieldValues,
            customFieldsValues: customFieldsValues,
            customFieldsList: customFields,
            workflowId: request.template.id,
            companyCurrency: request.companyCurrency,
            fieldSettings: journalEntryFieldSettings,
            requestCurrencyISOCode,
            defaultCurrencyRate,
            lines: itemList,
            attachmentsFieldSettings,
            attachments: request.attachments,
        });
    }, [
        setShowErrors,
        fieldValues,
        customFieldsValues,
        customFields,
        request.template.id,
        request.companyCurrency,
        request.attachments,
        journalEntryFieldSettings,
        requestCurrencyISOCode,
        defaultCurrencyRate,
        itemList,
        attachmentsFieldSettings,
    ]);

    return {
        getRequestTransfer,
        getValidationErrors,
    };
};
